/** Thin fetch wrapper: JSON in and out, cookies included, typed errors. */

export const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  tenantId?: string | null;
  signal?: AbortSignal;
}

let clientKind: 'web' | 'mobile' = 'web';
/** The mobile app tags its requests so the equipment history tells where an action came from. */
export function setClientKind(kind: 'web' | 'mobile') { clientKind = kind; }

export async function request<T>(path: string, { method = 'GET', body, tenantId, signal }: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (tenantId) headers['X-Tenant-Id'] = tenantId;
  if (clientKind === 'mobile') headers['X-Gonio-Client'] = 'mobile';

  let response: Response;
  try {
    response = await fetch(API_BASE + path, {
      method, headers, signal, credentials: 'same-origin',
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK_ERROR', 'Serveur injoignable, vérifiez votre connexion');
  }

  const text = await response.text();
  const payload = text ? safeJson(text) : null;
  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(response.status, error?.code ?? 'HTTP_ERROR', error?.message ?? `Erreur ${response.status}`, error?.details);
  }
  return payload as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Builds a URL for resources loaded directly by the browser (images, CSV, event streams). */
export function resourceUrl(path: string, tenantId?: string | null): string {
  const url = new URL(API_BASE + path, window.location.origin);
  if (tenantId) url.searchParams.set('tenant', tenantId);
  return url.pathname + url.search;
}
